import { Controller } from '@hotwired/stimulus'
import type { Chart, ChartConfiguration, ChartType, TooltipModel } from 'chart.js'

// Data from ChartsHelper (I-FEAT-50).
interface ChartDataset { label: string, data: number[], color?: string, colors?: string[] }
interface ChartPayload { type: 'line' | 'bar' | 'doughnut', labels: string[], datasets: ChartDataset[] }
interface Theme { primary: string, secondary: string, text: string, grid: string, surface: string }

// Draws one ChartsHelper chart with Chart.js; colours come from the daisyUI theme and follow a light/dark switch.
// The tooltip text is mirrored into a polite live region (the canvas itself is opaque to screen readers and tests).
export default class ChartController extends Controller<HTMLElement> {
  static override targets = ['canvas', 'readout']
  static override values = { config: Object }

  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly readoutTarget: HTMLElement
  declare readonly configValue: ChartPayload

  private chart: Chart | undefined
  private chartClass: typeof Chart | undefined
  private live = false
  private observer: MutationObserver | undefined
  private readonly scheme = matchMedia('(prefers-color-scheme: dark)')
  private readonly redraw = (): void => { this.draw() }

  override connect(): void {
    this.live = true
    void this.load()
    this.observer = new MutationObserver(this.redraw)
    this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    this.scheme.addEventListener('change', this.redraw)
  }

  override disconnect(): void {
    this.live = false
    this.observer?.disconnect()
    this.scheme.removeEventListener('change', this.redraw)
    this.chart?.destroy()
    this.chart = undefined
  }

  // Chart.js is a separate chunk (~60 kB gzip): only pages with a chart fetch it.
  private async load(): Promise<void> {
    const { Chart } = await import('~/chartjs')
    if (!this.live) return // navigated away while loading
    this.chartClass = Chart
    this.draw()
  }

  // Rebuilt from scratch on a theme switch: simpler than patching every colour of a live chart.
  private draw(): void {
    const Chart = this.chartClass
    if (!Chart) return
    this.chart?.destroy()
    const theme = this.theme()
    Chart.defaults.font.family = getComputedStyle(document.body).fontFamily
    Chart.defaults.color = theme.text
    const payload = this.configValue
    if (payload.type === 'line') this.chart = new Chart(this.canvasTarget, this.lineConfig(payload, theme))
    else if (payload.type === 'bar') this.chart = new Chart(this.canvasTarget, this.barConfig(payload, theme))
    else this.chart = new Chart(this.canvasTarget, this.doughnutConfig(payload, theme))
  }

  // Many years: the first series (the current year) bold in primary, the previous one in secondary, older ones thin grey
  // fading with age. A legend click hides/shows a year (Chart.js default).
  private lineConfig(payload: ChartPayload, theme: Theme): ChartConfiguration<'line'> {
    const datasets = payload.datasets.map((set, index) => {
      const color = index === 0 ? theme.primary : index === 1 ? theme.secondary : rgba(theme.text, Math.max(0.15, 0.5 - (index - 2) * 0.06))
      return {
        label: set.label, data: set.data, borderColor: color, backgroundColor: color, order: index,
        borderWidth: index === 0 ? 3 : index === 1 ? 2 : 1.25, tension: 0.3, pointRadius: 0, pointHoverRadius: 4,
      }
    })
    return {
      type: 'line',
      data: { labels: payload.labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: axes(theme),
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 12, boxHeight: 3, padding: 12 } },
          tooltip: { external: this.mirror },
        },
      },
    }
  }

  private barConfig(payload: ChartPayload, theme: Theme): ChartConfiguration<'bar'> {
    const datasets = payload.datasets.map((set) => ({
      label: set.label, data: set.data, backgroundColor: set.color ?? theme.primary, borderRadius: 4, maxBarThickness: 48,
    }))
    return {
      type: 'bar',
      data: { labels: payload.labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: axes(theme),
        plugins: { legend: { display: false }, tooltip: { external: this.mirror } },
      },
    }
  }

  // Slice colours are the countries' own colours (REP-209 Barva), not the theme's.
  private doughnutConfig(payload: ChartPayload, theme: Theme): ChartConfiguration<'doughnut'> {
    const datasets = payload.datasets.map((set) => ({
      label: set.label, data: set.data, backgroundColor: set.colors ?? theme.primary, borderColor: theme.surface, borderWidth: 2,
    }))
    return {
      type: 'doughnut',
      data: { labels: payload.labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: '60%',
        plugins: {
          legend: { position: 'right', labels: { boxWidth: 10, boxHeight: 10, padding: 8 } },
          // the title is already the country; the body would repeat the chart's name
          tooltip: { external: this.mirror, callbacks: { label: (item) => ` ${item.formattedValue}` } },
        },
      },
    }
  }

  private readonly mirror = ({ tooltip }: { tooltip: TooltipModel<ChartType> }): void => {
    // tooltip.opacity is still animating here, so it can't tell shown from hidden; the last reading simply stays.
    if (tooltip.dataPoints.length === 0) return
    this.readoutTarget.textContent = [...tooltip.title, ...tooltip.body.flatMap((part) => part.lines)].map((line) => line.trim()).join(' · ')
  }

  private theme(): Theme {
    const style = getComputedStyle(this.element)
    const read = (name: string): string => toRgb(style.getPropertyValue(name).trim())
    const text = read('--color-base-content')
    return { primary: read('--color-primary'), secondary: read('--color-secondary'), text, grid: rgba(text, 0.08), surface: read('--color-base-100') }
  }
}

// No vertical grid, faint horizontal lines, at most 5 value ticks.
function axes(theme: Theme): NonNullable<ChartConfiguration<'line'>['options']>['scales'] {
  return {
    x: { grid: { display: false }, border: { display: false }, ticks: { autoSkipPadding: 12, maxRotation: 0 } },
    y: { beginAtZero: true, grid: { color: theme.grid }, border: { display: false }, ticks: { maxTicksLimit: 5 } },
  }
}

// daisyUI colours are oklch(); Chart.js' colour helper can't parse them, so let the browser paint one pixel and read it back.
const probe = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
function toRgb(css: string): string {
  if (!probe || !css) return css || 'gray'
  probe.clearRect(0, 0, 1, 1)
  probe.fillStyle = css
  probe.fillRect(0, 0, 1, 1)
  const [r = 0, g = 0, b = 0] = probe.getImageData(0, 0, 1, 1).data
  return `rgb(${r}, ${g}, ${b})`
}

function rgba(rgb: string, alpha: number): string {
  return rgb.replace(/^rgb\((.*)\)$/, `rgba($1, ${alpha})`)
}
